import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ProductCard from "../components/ProductCard";
import { Truck, RefreshCw, ShieldCheck, ChevronRight, ChevronLeft } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { api, absUrl } from "../lib/api";
import { useSettings } from "../context/SettingsContext";

function WhatsAppIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.82 11.82 0 018.413 3.488 11.82 11.82 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.515 5.26l-.999 3.648 3.973-1.045zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
    </svg>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const { settings } = useSettings();
  const { toast } = useToast();
  const [product, setProduct] = useState(undefined); // undefined=loading, null=not found
  const [related, setRelated] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const touchStartX = React.useRef(null);

  const goNext = () => {
    if (!product) return;
    setActiveImg((i) => (i + 1) % product.images.length);
  };
  const goPrev = () => {
    if (!product) return;
    setActiveImg((i) => (i - 1 + product.images.length) % product.images.length);
  };
  const onTouchStart = (e) => { touchStartX.current = e.changedTouches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) {
      if (dx < 0) goNext();
      else goPrev();
    }
    touchStartX.current = null;
  };

  useEffect(() => {
    setProduct(undefined);
    setActiveImg(0);
    setSize("");
    setColor("");
    api.getProduct(slug).then(setProduct).catch(() => setProduct(null));
    api.getProducts({ tag: "best-seller" }).then(setRelated).catch(() => {});
  }, [slug]);

  if (product === undefined) {
    return (
      <div className="mx-auto max-w-[1400px] px-4 md:px-8 py-12 grid gap-10 md:grid-cols-2 animate-pulse">
        <div className="aspect-[3/4] bg-neutral-100" />
        <div className="space-y-4 pt-6">
          <div className="h-8 bg-neutral-100 w-2/3" />
          <div className="h-5 bg-neutral-100 w-1/4" />
          <div className="h-24 bg-neutral-100" />
          <div className="h-12 bg-neutral-100" />
        </div>
      </div>
    );
  }

  if (product === null) {
    return (
      <div className="mx-auto max-w-[1400px] px-4 py-32 text-center">
        <h1 className="font-display text-3xl">Product not found</h1>
        <Link to="/shop" className="mt-6 inline-block text-xs tracking-[0.2em] border-b border-black pb-1">
          BACK TO SHOP
        </Link>
      </div>
    );
  }

  const orderViaWhatsApp = () => {
    if (!size && product.sizes?.length) {
      toast({ title: "Please select a size", description: "Choose your size before ordering." });
      return;
    }
    if (!color && product.colors?.length) {
      toast({ title: "בחרי צבע", description: "יש לבחור צבע לפני ההזמנה." });
      return;
    }
    const url = `${window.location.origin}/product/${product.slug}`;
    const imageUrl = product.images && product.images.length ? absUrl(product.images[0]) : null;
    const lines = [
      `היי, אני מעוניין/ת במוצר הבא מ-${settings.brandName}:`,
      ``,
      `🛍️ ${product.name}`,
      `💰 מחיר: ₪${product.price.toFixed(2)}`,
      size ? `📏 מידה: ${size}` : null,
      color ? `🎨 צבע: ${color}` : null,
      ``,
      imageUrl ? `🖼️ תמונה: ${imageUrl}` : null,
      `🔗 ${url}`,
    ].filter(Boolean);
    const text = encodeURIComponent(lines.join("\n"));
    window.open(`https://wa.me/${settings.whatsapp}?text=${text}`, "_blank");
  };

  const relatedFiltered = related.filter((p) => p.id !== product.id).slice(0, 4);

  return (
    <div className="fade-up">
      <div className="mx-auto max-w-[1400px] px-4 md:px-8 pt-6">
        <p className="text-xs tracking-[0.2em] text-neutral-400 flex items-center gap-1">
          <Link to="/" className="hover:text-black">HOME</Link> <ChevronRight className="h-3 w-3" />
          <Link to="/shop" className="hover:text-black">SHOP</Link> <ChevronRight className="h-3 w-3" />
          <span className="text-neutral-700">{product.name.toUpperCase()}</span>
        </p>
      </div>

      <div className="mx-auto max-w-[1400px] px-4 md:px-8 py-8 grid gap-10 md:grid-cols-2">
        <div className="flex gap-4">
          {product.images.length > 1 && (
            <div className="hidden md:flex flex-col gap-3 w-20 shrink-0">
              {product.images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className={`aspect-[3/4] overflow-hidden border ${activeImg === i ? "border-black" : "border-neutral-200"}`}
                >
                  <img src={absUrl(img)} alt="thumb" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
          <div
            className="relative flex-1 overflow-hidden bg-neutral-100 aspect-[3/4]"
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            {product.images[activeImg] && (
              <img src={absUrl(product.images[activeImg])} alt={product.name} className="h-full w-full object-cover select-none" />
            )}
            {product.soldOut && (
              <span className="absolute top-4 left-4 bg-white/95 text-black text-[11px] tracking-[0.2em] px-4 py-1.5">
                SOLD OUT
              </span>
            )}

            {product.images.length > 1 && (
              <>
                <button
                  onClick={goPrev}
                  aria-label="previous image"
                  className="absolute left-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/85 hover:bg-white text-black flex items-center justify-center shadow transition-colors"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  onClick={goNext}
                  aria-label="next image"
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/85 hover:bg-white text-black flex items-center justify-center shadow transition-colors"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>

                <span className="absolute top-3 right-3 bg-black/60 text-white text-[11px] px-2.5 py-1 rounded-full">
                  {activeImg + 1}/{product.images.length}
                </span>

                <div className="absolute bottom-3 left-0 right-0 flex items-center justify-center gap-1.5">
                  {product.images.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveImg(i)}
                      aria-label={`image ${i + 1}`}
                      className={`h-1.5 rounded-full transition-all ${
                        activeImg === i ? "w-5 bg-white" : "w-1.5 bg-white/60"
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Mobile thumbnails row */}
        {product.images.length > 1 && (
          <div className="md:hidden flex gap-2 overflow-x-auto pb-1 -mt-4">
            {product.images.map((img, i) => (
              <button
                key={i}
                onClick={() => setActiveImg(i)}
                className={`h-20 w-16 shrink-0 overflow-hidden border ${activeImg === i ? "border-black" : "border-neutral-200"}`}
              >
                <img src={absUrl(img)} alt="thumb" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        )}

        <div className="md:pt-4">
          <p className="text-xs tracking-[0.3em] text-neutral-400">{settings.brandName}</p>
          <h1 className="font-display text-3xl md:text-4xl tracking-wide mt-2">{product.name}</h1>
          <div className="mt-4 flex items-center gap-3">
            {product.compareAt && (
              <span className="text-lg text-neutral-400 line-through">₪{product.compareAt.toFixed(2)}</span>
            )}
            <span className="text-xl text-neutral-900">₪{product.price.toFixed(2)}</span>
          </div>

          <p className="mt-6 text-sm leading-relaxed text-neutral-600 max-w-prose">
            {product.description}
          </p>

          {product.sizes?.length > 0 && (
            <div className="mt-8">
              <p className="text-xs tracking-[0.2em] text-neutral-700 mb-3">SIZE</p>
              <div className="flex flex-wrap gap-2">
                {product.sizes.map((s) => (
                  <button
                    key={s}
                    onClick={() => setSize(s)}
                    className={`min-w-[48px] px-4 py-2.5 text-sm border transition-colors ${size === s ? "bg-black text-white border-black" : "border-neutral-300 hover:border-black"}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {product.colors?.length > 0 && (
            <div className="mt-8">
              <p className="text-xs tracking-[0.2em] text-neutral-700 mb-3">
                COLOR{color ? <span className="text-neutral-400 tracking-normal"> — {color}</span> : null}
              </p>
              <div className="flex flex-wrap gap-3">
                {product.colors.map((c) => (
                  <button
                    key={c.name}
                    onClick={() => setColor(c.name)}
                    data-testid={`color-option-${c.name}`}
                    title={c.name}
                    className="flex flex-col items-center gap-1.5"
                  >
                    <span
                      className={`h-9 w-9 rounded-full border-2 transition-all ${
                        color === c.name ? "border-black ring-2 ring-black ring-offset-2" : "border-neutral-300 hover:border-black"
                      }`}
                      style={{ backgroundColor: c.hex }}
                    />
                    <span className={`text-[11px] ${color === c.name ? "text-black font-medium" : "text-neutral-500"}`}>{c.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <button
            onClick={orderViaWhatsApp}
            disabled={product.soldOut}
            className={`mt-8 w-full flex items-center justify-center gap-3 py-4 text-sm tracking-[0.2em] transition-colors ${
              product.soldOut
                ? "bg-neutral-200 text-neutral-400 cursor-not-allowed"
                : "bg-[#25D366] text-white hover:bg-[#1ebe5d]"
            }`}
          >
            <WhatsAppIcon className="h-5 w-5" />
            {product.soldOut ? "SOLD OUT" : "ORDER VIA WHATSAPP"}
          </button>
          <p className="mt-3 text-center text-xs text-neutral-400">
            לחיצה תשלח הודעה עם פרטי המוצר ישירות לוואטסאפ
          </p>

          <div className="mt-8 grid grid-cols-3 gap-4 border-t border-neutral-200 pt-6">
            <div className="flex flex-col items-center text-center gap-2">
              <Truck className="h-5 w-5 text-neutral-700" />
              <span className="text-[11px] text-neutral-500 tracking-wide">Fast shipping</span>
            </div>
            <div className="flex flex-col items-center text-center gap-2">
              <RefreshCw className="h-5 w-5 text-neutral-700" />
              <span className="text-[11px] text-neutral-500 tracking-wide">Easy returns</span>
            </div>
            <div className="flex flex-col items-center text-center gap-2">
              <ShieldCheck className="h-5 w-5 text-neutral-700" />
              <span className="text-[11px] text-neutral-500 tracking-wide">Secure order</span>
            </div>
          </div>
        </div>
      </div>

      {relatedFiltered.length > 0 && (
        <section className="mx-auto max-w-[1400px] px-4 md:px-8 py-16">
          <h2 className="font-display text-2xl md:text-3xl tracking-wide mb-8 text-center">YOU MAY ALSO LOVE</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-8">
            {relatedFiltered.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}
    </div>
  );
}
